/** SPA-account entry module — Phase AC.6.
 *
 * Layer: `orchestrator` per docs/design/spa-account-extraction-260524.md §3.4.
 *
 * This is the `<script type="module" src="/static/spa-account/init.js">` target
 * for `web/static/account.html`. It imports from all controller layers and
 * wires up every DOM event listener and page-load fetch that previously lived
 * in the inline `<script type="module">` block (account.html lines 357–787).
 *
 * Responsibility scope:
 *   - Module-level esc / authFetch shims from window globals.
 *   - Construct ApiTokensController (class, AC.5) and AccountActionsController
 *     (class, AC.5) — both take { esc, authFetch } deps.
 *   - Create the usage controller via createUsageController() — factory, AC.5
 *     — injecting loadApiTokensFn callback to avoid peer-controller import.
 *   - Call attachSubscriptionController() + attachGroupsController() (no-ops
 *     today; placeholder for future wiring).
 *   - Call .attach() on each class controller to bind document listeners.
 *   - Preserve the .manage-history-link token-forwarding handler (was at
 *     account.html lines 774–783; spa/init.js is NOT loaded on account.html
 *     so this handler is not covered elsewhere).
 *   - Call usageController.loadAccount() to kick off the page load.
 *
 * No exports — this module is the account.html entry point.
 * Nothing imports from it.
 */
import { ApiTokensController } from './controller-api-tokens.js';
import { AccountActionsController } from './controller-account-actions.js';
import { createUsageController } from './controller-usage.js';
import { attachSubscriptionController } from './controller-subscription.js';
import { attachGroupsController } from './controller-groups.js';
import { getAuthFetch, getAccessToken, setHandoffToken } from '../spa-shared/auth-surface.js';
// ─── Window globals ───────────────────────────────────────────────────────────
//
// WHY type assertion: `adaEsc` is injected by a sibling script tag in
// account.html (esc.js); TypeScript cannot see it at compile time.
//
// The Supabase auth surface is NO LONGER read here — it goes through the
// canonical `../spa-shared/auth-surface.js` seam (getAuthFetch / getAccessToken /
// setHandoffToken). This bundle previously read the retired flat global
// `window.AdaSupabase` (undefined post window-adatool-ns migration 4899f24b63),
// which broke the Bearer header → 401 → signed-out History/Account. Epic
// cross-spa-auth-propagation-260705 Phase 2.
const _win = window;
const esc = _win.adaEsc ?? ((s) => String(s));
// Canonical Bearer-attaching authFetch (fails LOUD, not silent, if the shared
// surface is somehow absent — see auth-surface.ts).
const authFetch = getAuthFetch();
// ─── Construct controllers ────────────────────────────────────────────────────
// ApiTokensController is constructed first so its loadApiTokens() method can
// be injected into createUsageController() as loadApiTokensFn — avoiding a
// peer-controller import (per AC.5 cross-controller note in controller-usage.ts).
const apiTokens = new ApiTokensController({
    esc,
    authFetch,
    doc: document,
    nav: navigator,
});
const accountActions = new AccountActionsController({
    authFetch,
    doc: document,
    fetch: window.fetch.bind(window),
    localStorage: window.localStorage,
    location: window.location,
});
const usageController = createUsageController({
    esc,
    authFetch,
    fetch: window.fetch.bind(window),
    doc: document,
    loadApiTokensFn: () => apiTokens.loadApiTokens(),
});
// ─── Attach listeners ─────────────────────────────────────────────────────────
apiTokens.attach();
accountActions.attach();
attachSubscriptionController();
attachGroupsController();
// ─── Token forwarding for /history nav link ───────────────────────────────────
//
// WHY here and not in a controller: spa/init.js is NOT loaded on account.html
// (confirmed: no <script src="/static/spa/init.js"> in account.html), so this
// handler has no other home. It belongs in the orchestrator that owns the
// account.html entry point.
document.addEventListener('click', async (e) => {
    const link = e.target?.closest('.manage-history-link');
    if (!link)
        return;
    e.preventDefault();
    const token = await getAccessToken();
    if (token)
        setHandoffToken(token);
    window.location.href = '/history';
});
// ─── Kick off page load ───────────────────────────────────────────────────────
void usageController.loadAccount();
